import { action, observable, runInAction } from 'mobx'
import ListStore from '../../../shared-lib/stores/ListStore'
import EmailCampaignModel from './EmailCampaignModel'
import { CampaignTemplateTypeValues } from '../../lib/crmHelper'

export type $EmailCampaignStore = EmailCampaignStore

export const contentTypeToText: Record<EmailContentBlockType, string> = {
  title: 'Title',
  text: 'Text',
  boldText: 'Subtitle',
  image: 'Image',
  button: 'Button',
  aftercareButton: 'Aftercare',
  horizontalLine: 'Horizontal Line',
  headerImage: 'Header Image',
  promocode: 'Promo code',
  ctas: 'Call to action',
  cancellationPolicy: 'Cancellation policy',
  bookingDateAndLocation: 'Booking date and location',
  bookingItems: 'Booking items and payments',
  declineReason: 'Decline reason',
  loyaltyScheme: 'Loyalty scheme',
  clientReferralScheme: 'Client referral scheme',
  socialLinks: 'Social links',
  giftCardDetails: 'Gift card details',
  giftCardReceipt: 'Gift card payment',
}

export default class EmailCampaignStore extends ListStore<EmailCampaignModel> {
  fetchMethodName = 'emailCampaigns'
  ModelClass = EmailCampaignModel
  @observable accessor emailTemplates: Record<CampaignTemplateTypeValues, CampaignTemplate> | undefined
  templatesVersion = 1

  @action.bound async createEmailCampaign_api(data: CreateEmailCampaignInput, showConfirmationMessage = true) {
    const { communication: { requester }, dropdownMessageHandler: { showDropdownMessage } } = this.rootStore

    this.setLoading(true)
    try {
      const { createEmailCampaign } = await requester.createEmailCampaign({ data })
      this.mergeData([createEmailCampaign])

      if (showConfirmationMessage) {
        showDropdownMessage({
          title: 'Campaign created successfully.',
          type: 'success',
        })
      }
      return createEmailCampaign
    } catch (e) {
      return false
    } finally {
      this.setLoading(false)
    }
  }

  @action.bound deleteOldImages = async () => {
    const { communication: { requester } } = this.rootStore

    if (this.editItem && this.editItem.imagesToDelete.length > 0) {
      this.setLoading(true)

      try {
        await Promise.all(this.editItem.imagesToDelete.map(async image => {
          await requester.deleteFile({ filename: image, type: 'saas' })
        }))
      } catch (e) {
        return
      } finally {
        this.setLoading(false)
      }

      this.editItem.imagesToDelete = []
    }
  }

  @action.bound async updateEmailCampaign_api(data: UpdateEmailCampaignInput) {
    const { communication: { requester } } = this.rootStore

    this.setLoading(true)

    delete (data as EmailCampaign).lastSentAt

    // Cleanup empty promocodeId
    data.emailContent?.forEach(contentBlock => {
      if (contentBlock.data?.promocodeId === '') {
        contentBlock.data = undefined
      }
    })

    try {
      const { updateEmailCampaign } = await requester.updateEmailCampaign({ data })
      this.mergeData([updateEmailCampaign])
      return updateEmailCampaign
    } catch (e) {
      return
    } finally {
      this.setLoading(false)
    }
  }

  @action setEditItem = (data: EmailCampaignModel['data']) => {
    this.originalEditItemData = { ...data }
    this.editItem = new this.ModelClass(data, this.rootStore) as EmailCampaignModel

    const existingCampaign = this.findItemById(data._id)
    if (existingCampaign) {
      this.editItem.automatedCampaignData = existingCampaign.automatedCampaignData
    }
    this.editItem.imagesToDelete = []
  }

  @action.bound async fetchEmailTemplates() {
    const { communication: { requester } } = this.rootStore
    if (!this.emailTemplates) {
      const { getEmailTemplates: { emailTemplates } } = await requester.getEmailTemplates()
      runInAction(() => {
        this.emailTemplates = emailTemplates
      })
    }
  }
}
